import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { actions } from '.';
import {
  LawyerConnection,
  LawyerConnectionUser,
  Message,
  File as AMFile,
  Lawyer,
} from '@law-connect/types';

interface LawyerConnectionState {
  pending: {
    fetchById?: boolean;
    fetchMessages?: boolean;
    sendMessage?: boolean;
    requestPermission?: boolean;
    requestClientIntake?: boolean;
    fetchUser?: boolean;
    markDeleted?: boolean;
    markEnded?: boolean;
    fetchLawyersByIds?: boolean;
    requestContactInformation?: boolean;
  };
  errors: {
    fetchById?: string | null;
    fetchMessages?: string | null;
    sendMessage?: string | null;
    requestPermission?: string | null;
    requestClientIntake?: string | null;
    fetchUser?: string | null;
    markDeleted?: string | null;
    markEnded?: string | null;
    fetchLawyersByIds?: string | null;
    requestContactInformation?: string | null;
  };
  connections: {
    // we have this structure so when updating the connection we dont overwrite the messages
    [id: string]: {
      connection: LawyerConnection;
      messages: Message[];
      user?: LawyerConnectionUser | null;
      lawyers?: Lawyer[] | null;
    };
  };
}

const lawyerConnectionState: LawyerConnectionState = {
  pending: {},
  errors: {},
  connections: {},
};

const lawyerConnectionSlice = createSlice({
  name: 'lawyerConnection',
  initialState: lawyerConnectionState,
  // you cant have a reducer name match the thunk action
  reducers: {
    pollMessages: (state, action) => {
      // do nothing and handled by middleware
    },
    stopPollMessages: (state, action) => {
      // do nothing and handled by middleware
    },
    addMessage: (
      state,
      action: PayloadAction<{ connectionId: string; message: Message }>
    ) => {
      if (state.connections[action.payload.connectionId]) {
        const message = state.connections[
          action.payload.connectionId
        ].messages.find((m) => m.id === action.payload.message.id);
        if (message) {
          // If message exists, we can update the readAt value
          message.readAt = action.payload.message.readAt;

          // update the connection readAt as well to render the message as read
          state.connections[action.payload.connectionId].connection.readAt = 
            action.payload.message.readAt;
        } else {
          state.connections[action.payload.connectionId].messages.push(
            action.payload.message
          );
        }
      }
    },
    updateEmailOpenedAt: (
      state,
      action: PayloadAction<{ connectionId: string; emailOpenedAt: number }>
    ) => {
      if (state.connections[action.payload.connectionId]) {
        state.connections[action.payload.connectionId].connection.emailOpenedAt 
        = action.payload.emailOpenedAt;
      }
    }
  },
  extraReducers: (builder) => {
    // fetch single connection by id
    builder.addCase(actions.lawyerConnection.fetchById.pending, (state) => {
      state.errors.fetchById = null;
    });
    builder.addCase(
      actions.lawyerConnection.fetchById.fulfilled,
      (state, action) => {
        state.errors.fetchById = null;

        if (action.payload) {
          if (!state.connections[action.payload.id]) {
            state.connections[action.payload.id] = {
              connection: action.payload,
              messages: [],
            };
          } else {
            state.connections[action.payload.id].connection = {
              ...state.connections[action.payload.id].connection,
              ...action.payload,
            };
          }
        }
      }
    );
    builder.addCase(
      actions.lawyerConnection.fetchById.rejected,
      (state, action) => {
        state.errors.fetchById = action.error.message;
      }
    );
    // fetch messages
    builder.addCase(actions.lawyerConnection.fetchMessages.pending, (state) => {
      state.pending.fetchMessages = true;
      state.errors.fetchMessages = null;
    });
    builder.addCase(
      actions.lawyerConnection.fetchMessages.fulfilled,
      (state, action) => {
        state.pending.fetchMessages = false;
        state.errors.fetchMessages = null;
        if (!state.connections[action.meta.arg.id]) {
          state.connections[action.meta.arg.id] = {
            connection: null,
            messages: action.payload,
          };
        } else {
          state.connections[action.meta.arg.id].messages = action.payload;
        }
      }
    );
    builder.addCase(
      actions.lawyerConnection.fetchMessages.rejected,
      (state, action) => {
        state.pending.fetchMessages = false;
        state.errors.fetchMessages = action.error.message;
      }
    );
    // send message
    builder.addCase(actions.lawyerConnection.sendMessage.pending, (state) => {
      state.pending.sendMessage = true;
      state.errors.sendMessage = null;
    });
    builder.addCase(
      actions.lawyerConnection.sendMessage.fulfilled,
      (state, action) => {
        state.pending.sendMessage = false;
        state.errors.sendMessage = null;
        if (!state.connections[action.meta.arg.id]) {
          state.connections[action.meta.arg.id] = {
            connection: null,
            messages: [action.payload],
          };
        } else if (
          !state.connections[action.meta.arg.id].messages
            .map((m) => m.id)
            .includes(action.payload.id)
        ) {
          state.connections[action.meta.arg.id].messages.push(action.payload);
        }
      }
    );
    builder.addCase(
      actions.lawyerConnection.sendMessage.rejected,
      (state, action) => {
        state.pending.sendMessage = false;
        state.errors.sendMessage = action.error.message;
      }
    );
    // requestPermission
    builder.addCase(
      actions.lawyerConnection.requestPermission.pending,
      (state) => {
        state.pending.requestPermission = true;
        state.errors.requestPermission = null;
      }
    );
    builder.addCase(
      actions.lawyerConnection.requestPermission.fulfilled,
      (state, action) => {
        state.pending.requestPermission = false;
        if (!state.connections[action.meta.arg.id]) {
          state.connections[action.meta.arg.id] = {
            connection: null,
            messages: [action.payload],
          };
        } else if (
          !state.connections[action.meta.arg.id].messages
            .map((m) => m.id)
            .includes(action.payload.id)
        ) {
          state.connections[action.meta.arg.id].messages.push(action.payload);
        }
      }
    );
    builder.addCase(
      actions.lawyerConnection.requestPermission.rejected,
      (state, action) => {
        state.pending.requestPermission = false;
        state.errors.requestPermission = action.error.message;
      }
    );
    // requestClientIntake
    builder.addCase(
      actions.lawyerConnection.requestClientIntake.pending,
      (state) => {
        state.pending.requestClientIntake = true;
        state.errors.requestClientIntake = null;
      }
    );
    builder.addCase(
      actions.lawyerConnection.requestClientIntake.fulfilled,
      (state, action) => {
        state.pending.requestClientIntake = false;
        if (!state.connections[action.meta.arg.id]) {
          state.connections[action.meta.arg.id] = {
            connection: null,
            messages: [action.payload],
          };
        } else if (
          !state.connections[action.meta.arg.id].messages
            .map((m) => m.id)
            .includes(action.payload.id)
        ) {
          state.connections[action.meta.arg.id].messages.push(action.payload);
        }
      }
    );
    builder.addCase(
      actions.lawyerConnection.requestClientIntake.rejected,
      (state, action) => {
        state.pending.requestClientIntake = false;
        state.errors.requestClientIntake = action.error.message;
      }
    );
    // fetch user
    builder.addCase(actions.lawyerConnection.fetchUser.pending, (state) => {
      state.pending.fetchUser = true;
      state.errors.fetchUser = null;
    });
    builder.addCase(
      actions.lawyerConnection.fetchUser.fulfilled,
      (state, action) => {
        state.pending.fetchUser = false;
        state.errors.fetchUser = null;
        if (!state.connections[action.meta.arg.connectionId]) {
          state.connections[action.meta.arg.connectionId] = {
            connection: null,
            messages: [],
            user: action.payload,
          };
        } else {
          state.connections[action.meta.arg.connectionId].user = action.payload;
        }
      }
    );
    builder.addCase(
      actions.lawyerConnection.fetchUser.rejected,
      (state, action) => {
        state.pending.fetchUser = false;
        state.errors.fetchUser = action.error.message;
      }
    );
    // fetch lawyers by ids
    builder.addCase(
      actions.lawyerConnection.fetchLawyersByIds.pending,
      (state) => {
        state.pending.fetchLawyersByIds = true;
        state.errors.fetchLawyersByIds = null;
      }
    );
    builder.addCase(
      actions.lawyerConnection.fetchLawyersByIds.fulfilled,
      (state, action) => {
        state.pending.fetchLawyersByIds = false;
        state.errors.fetchLawyersByIds = null;
        if (!state.connections[action.meta.arg.connectionId]) {
          state.connections[action.meta.arg.connectionId] = {
            connection: null,
            messages: [],
            lawyers: action.payload,
          };
        } else {
          // add the payload to the existing lawyers
          state.connections[action.meta.arg.connectionId].lawyers = [
            ...(state.connections[action.meta.arg.connectionId].lawyers || []),
            ...action.payload,
          ];
        }
      }
    );
    builder.addCase(
      actions.lawyerConnection.fetchLawyersByIds.rejected,
      (state, action) => {
        state.pending.fetchLawyersByIds = false;
        state.errors.fetchLawyersByIds = action.error.message;
      }
    );
    // mark deleted
    builder.addCase(actions.lawyerConnection.markDeleted.pending, (state) => {
      state.pending.markDeleted = true;
      state.errors.markDeleted = null;
    });
    builder.addCase(
      actions.lawyerConnection.markDeleted.fulfilled,
      (state, action) => {
        state.pending.markDeleted = false;
        state.errors.markDeleted = null;
        if (state.connections[action.meta.arg.id]?.connection) {
          state.connections[action.meta.arg.id].connection.deletedAt =
            Date.now();
        }
      }
    );
    builder.addCase(
      actions.lawyerConnection.markDeleted.rejected,
      (state, action) => {
        state.pending.markDeleted = false;
        state.errors.markDeleted = action.error.message;
      }
    );
    // mark ended
    builder.addCase(actions.lawyerConnection.markEnded.pending, (state) => {
      state.pending.markEnded = true;
      state.errors.markEnded = null;
    });
    builder.addCase(
      actions.lawyerConnection.markEnded.fulfilled,
      (state, action) => {
        state.pending.markEnded = false;
        state.errors.markEnded = null;
        if (state.connections[action.meta.arg.id]?.connection) {
          state.connections[action.meta.arg.id].connection.endedAt = Date.now();
        }
      }
    );
    builder.addCase(
      actions.lawyerConnection.markEnded.rejected,
      (state, action) => {
        state.pending.markEnded = false;
        state.errors.markEnded = action.error.message;
      }
    );
    // requestContactInformation
    builder.addCase(
      actions.lawyerConnection.requestContactInformation.pending,
      (state) => {
        state.pending.requestContactInformation = true;
        state.errors.requestContactInformation = null;
      }
    );
    builder.addCase(
      actions.lawyerConnection.requestContactInformation.fulfilled,
      (state, action) => {
        state.pending.requestContactInformation = false;
        if (!state.connections[action.meta.arg.id]) {
          state.connections[action.meta.arg.id] = {
            connection: null,
            messages: [action.payload],
          };
        } else if (
          !state.connections[action.meta.arg.id].messages
            .map((m) => m.id)
            .includes(action.payload.id)
        ) {
          state.connections[action.meta.arg.id].messages.push(action.payload);
        }
      }
    );
    builder.addCase(
      actions.lawyerConnection.requestContactInformation.rejected,
      (state, action) => {
        state.pending.requestContactInformation = false;
        state.errors.requestContactInformation = action.error.message;
      }
    );
  },
});

// im not sure if this is good practice but it mimics what we used to do with dispatching actions
export const lawyerConnectionActions = lawyerConnectionSlice.actions;
export const lawyerConnectionReducer = lawyerConnectionSlice.reducer;
